import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { BookOpen, Clock, Calendar, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentCourses = () => {
  const { token } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/enrollments/my`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnrollments(response.data);
      } catch (error) {
        console.error('Error fetching enrollments:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEnrollments();
  }, [token]);

  return (
    <DashboardLayout title="My Courses">
      {loading ? (
        <div className="grid md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6 h-48"></CardContent>
            </Card>
          ))}
        </div>
      ) : enrollments.length > 0 ? (
        <div className="grid md:grid-cols-2 gap-6">
          {enrollments.map((enrollment) => (
            <Card key={enrollment.id} className="card-hover overflow-hidden" data-testid={`course-card-${enrollment.id}`}>
              <div className="h-32 bg-gradient-to-r from-primary to-accent flex items-center justify-center relative">
                <BookOpen className="w-16 h-16 text-white/30" />
                <div className="absolute top-4 left-4">
                  <Badge className="bg-white/20 text-white">{enrollment.course?.category}</Badge>
                </div>
              </div>
              <CardContent className="p-6">
                <h3 className="font-heading font-semibold text-lg mb-2">{enrollment.course?.name || 'Course'}</h3>
                
                <div className="grid grid-cols-2 gap-3 mb-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span>{enrollment.course?.duration || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>{enrollment.enrollment_date?.split('T')[0]}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{enrollment.progress || 0}%</span>
                  </div>
                  <Progress value={enrollment.progress || 0} className="h-2" />
                </div>

                <div className="flex gap-2">
                  <Badge className={enrollment.status === 'active' ? 'bg-green-100 text-green-700' : ''}>
                    {enrollment.status}
                  </Badge>
                  {enrollment.discount_applied > 0 && (
                    <Badge variant="secondary">₹{enrollment.discount_applied} saved</Badge>
                  )}
                </div>

                <div className="flex gap-2 mt-4">
                  <Button variant="outline" className="flex-1" asChild>
                    <Link to="/dashboard/syllabus">View Syllabus</Link>
                  </Button>
                  <Button className="flex-1" asChild>
                    <Link to="/dashboard/materials">
                      Materials
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <BookOpen className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="font-heading text-xl font-semibold mb-2">No Enrolled Courses</h3>
            <p className="text-muted-foreground mb-6">You haven't enrolled in any courses yet.</p>
            <Button asChild data-testid="browse-courses-btn">
              <Link to="/courses">Browse Courses</Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </DashboardLayout>
  );
};

export default StudentCourses;
